import { gasPrice as defaultGasPrice, loadWeb3, maxGasForTokenSend } from './wallet';

const toHex = (value) => {
    if (value == null) return null;
    if (typeof value === 'number') return `0x${value.toString(16)}`;
    if (typeof value === 'string') {
        if (value.startsWith('0x')) return value;
        if (/^\d+$/.test(value)) return `0x${parseInt(value, 10).toString(16)}`;
        return `0x${value}`;
    }
    return value;
};

/**
 * Signs an ERC-20 transfer tx using backend-provided transactionDetails.
 * Never re-encodes data — uses tokenContract, data, nonce verbatim.
 * gasPrice falls back to legacy 10 Gwei when backend omits it (same as pre-v2 wallet sends).
 * gasLimit falls back to maxGasForTokenSend (600000). chainId from backend or RPC node.
 *
 * @param {object} transactionDetails — from iniciarpago response
 * @param {string} fundsKey — hex private key without 0x prefix
 * @returns {{ nonce, gasPrice, gas, to, value, data, v, r, s }}
 */
export default async function signTransaction(transactionDetails, fundsKey) {
    if (!transactionDetails?.data) {
        throw new Error('transactionDetails.data is required');
    }
    if (!transactionDetails.tokenContract) {
        throw new Error('transactionDetails.tokenContract is required');
    }

    const resolvedGasPrice =
        transactionDetails.gasPrice ?? transactionDetails.gas_price ?? defaultGasPrice;
    const resolvedGasLimit =
        transactionDetails.gasLimit ??
        transactionDetails.gas_limit ??
        transactionDetails.gas ??
        maxGasForTokenSend;

    const nonce =
        transactionDetails.nonceHex != null
            ? toHex(transactionDetails.nonceHex)
            : toHex(transactionDetails.nonce);
    const gas = toHex(resolvedGasLimit);
    const gasPrice = toHex(resolvedGasPrice);

    const w3 = loadWeb3(fundsKey);
    const chainId =
        transactionDetails.chainId ?? transactionDetails.chain_id ?? (await w3.eth.getChainId());

    const rawTx = {
        nonce,
        gasPrice,
        gas,
        to: transactionDetails.tokenContract,
        value: transactionDetails.value ?? '0x0',
        data: transactionDetails.data,
        chainId,
    };

    const privateKey = fundsKey.startsWith('0x') ? fundsKey : `0x${fundsKey}`;
    const signed = await w3.eth.accounts.signTransaction(rawTx, privateKey);

    // Mock/backend validate signedTransaction.nonce against transactionDetails.nonce (number).
    const nonceForPayload =
        transactionDetails.nonceHex != null
            ? toHex(transactionDetails.nonceHex)
            : toHex(transactionDetails.nonce);

    return {
        nonce: nonceForPayload,
        gasPrice,
        gas,
        to: transactionDetails.tokenContract,
        value: rawTx.value,
        data: transactionDetails.data,
        v: signed.v,
        r: signed.r,
        s: signed.s,
    };
}
